import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Nullable } from 'global';
import { quoteAPI } from 'modules/quote';
import { Button, Stack } from '@mui/material';
import { Restore } from '@mui/icons-material';
import { useFormContext } from 'react-hook-form';
import { QuoteChangelogListing } from 'modules/quote';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import { VendorOpportunity } from 'modules/vendorOpportunity';
import { QuoteDepositType } from '@trnsact/trnsact-shared-types';

import { getCompletedStepsFromEntity, getFirstIncompleteStep, mapEquipmentSourceToItem } from '../lib';
import { useDealBuilderPersistence, useDealBuilderStepNavigation } from '../hooks';
import { DealBuilderSteps } from './DealBuilderSteps';
import { DealBuilderContextProvider } from '../context';
import { QuoteHistory } from './components/QuoteHistory';
import { StepFooterNav } from './components/StepFooterNav';
import { DealBuilderSkeleton } from './DealBuilderSkeleton';
import { BuildAQuoteCard } from './components/BuildAQuoteCard';
import { dealBuilderApi, InventoryEquipmentItem } from '../api';
import { DealBuilderFormValues, DealBuilderEquipmentSource, DealBuilderCreditApplicationSource } from '../types';

interface Props {
  vo: VendorOpportunity;
  activeProposalId: Nullable<string>;
  shouldAutoLand: boolean;
  onRemount: (proposalId?: string, shouldAutoLand?: boolean) => void;
  creditApplication: DealBuilderCreditApplicationSource;
  equipmentData: Nullable<DealBuilderEquipmentSource[]>;
}

export const DealBuilderWrapper = ({
  vo,
  creditApplication,
  equipmentData,
  activeProposalId,
  shouldAutoLand,
  onRemount,
}: Props) => {
  const [render, setRender] = useState<'builder' | 'log'>('builder');
  const { landOnStep } = useDealBuilderStepNavigation();
  const { showNotification } = useNotifications();
  const { setValue } = useFormContext<DealBuilderFormValues>();

  const [startingNewQuote, setStartingNewQuote] = useState<boolean>(false);
  const [isEquipmentSaving, setEquipmentSaving] = useState<boolean>(false);

  const [selectedEquipmentItem, setSelectedEquipmentItem] = useState<InventoryEquipmentItem | null>(null);

  // Read via ref so the auto-populate effect isn't retriggered by unrelated re-fetches of this list
  const equipmentDataRef = useRef(equipmentData);
  equipmentDataRef.current = equipmentData;

  const [fetchEquipmentById] = useLazyQuery<{ equipment: DealBuilderEquipmentSource }, { id: string }>(
    dealBuilderApi.getEquipmentDetailsById
  );

  const {
    initialData,
    activeEntity,
    contactsData,
    setActiveEntity,
    historyEntities,
    loading: dealBuilderLoading,
    startNewQuote: startNewQuoteRaw,
  } = useDealBuilderPersistence(vo, activeProposalId, creditApplication?.id ?? null);

  const isLandedRef = useRef(false);
  useEffect(() => {
    if (!shouldAutoLand || isLandedRef.current || dealBuilderLoading || startingNewQuote || !activeEntity) return;

    isLandedRef.current = true;
    landOnStep(getFirstIncompleteStep(getCompletedStepsFromEntity(activeEntity)));
  }, [shouldAutoLand, dealBuilderLoading, startingNewQuote, activeEntity, landOnStep]);

  const activeQuoteId = activeEntity?.quote?.quoteId;

  const { data, loading: isQuoteCalcLoading } = useQuery(quoteAPI.getQuoteCalcById, {
    skip: !activeQuoteId,
    onCompleted: console.log,
    notifyOnNetworkStatusChange: true,
    variables: {
      quoteId: activeQuoteId,
    },
  });

  const priceCalc = data?.quoteCalc?.result?.entity ?? null;

  useEffect(() => {
    if (!initialData || dealBuilderLoading) return;

    //Auto-populate Contacts
    setValue('contacts.ownerPgIds', initialData.ownerPgIds ?? []);
    setValue('contacts.accountContactIds', initialData.accountContactIds ?? []);

    //Auto-populate Equipment
    if (initialData.equipmentId) {
      setValue('equipment.selectedId', initialData.equipmentId);
      const source = equipmentDataRef.current?.find(e => e.equipmentId === initialData.equipmentId);
      if (source) {
        setSelectedEquipmentItem(mapEquipmentSourceToItem(source));
      } else {
        fetchEquipmentById({ variables: { id: initialData.equipmentId } }).then(({ data }) => {
          if (data?.equipment) setSelectedEquipmentItem(mapEquipmentSourceToItem(data.equipment));
        });
      }
    }

    //Auto-populate Pricing
    if (initialData.quote?.deposit) {
      const { type, value, notes } = initialData.quote.deposit;
      setValue('pricing.customerDeposit.type', type as QuoteDepositType);
      setValue('pricing.customerDeposit.value', value as any);
      setValue('pricing.customerDeposit.notes', notes ?? '');
    }

    //Auto-populate Trade-In
    if (initialData.quote?.equipmentTradeIns?.length) {
      Promise.all(
        initialData.quote.equipmentTradeIns.map(async tradeIn => {
          const { data } = await fetchEquipmentById({
            variables: {
              id: tradeIn.equipmentId,
            },
          });

          const equipment = data?.equipment;

          return {
            netTrade: null,
            make: equipment?.make ?? null,
            year: equipment?.year ?? null,
            model: equipment?.model ?? null,
            equipmentId: tradeIn.equipmentId,
            newUsed: equipment?.newUsed ?? null,
            mileage: equipment?.mileage ?? null,
            serialNumberVin: equipment?.serialNumberVin ?? tradeIn.vin ?? null,

            tradeInValue: tradeIn.tradeInDetails.tradeValue,
            amountOwed: tradeIn.tradeInDetails.tradeAmountOwed,
            actualCashValue: (tradeIn.tradeInDetails as any).actualCashValue,
            overUnderAllowance: (tradeIn.tradeInDetails as any).overUnderAllowance,
            payoffLienholder: (tradeIn.tradeInDetails as any).tradePayoffLienholder ?? null,
            payoffAccountNumber: (tradeIn.tradeInDetails as any).tradePayoffAccountNumber ?? null,
            payoffGoodThroughDate: (tradeIn.tradeInDetails as any).tradePayoffGoodThroughDate ?? null,
          };
        })
      ).then(rows => setValue('pricing.tradeIn', rows));
    }

    //Auto-populate Finance&Menu
    setValue('financeMenu.isEnabled', !!initialData.proposal?.settings?.configuration?.formData);
    const savedSaleType = initialData.proposal?.settings?.configuration?.formData?.saleDetails?.saleType;
    if (savedSaleType) {
      setValue('financeMenu.saleType', savedSaleType);
    }
  }, [initialData, dealBuilderLoading, fetchEquipmentById, setValue]);

  const handleSetSelectedEquipmentItem = useCallback((item: InventoryEquipmentItem | null) => {
    setSelectedEquipmentItem(item);
  }, []);

  const startNewQuote = useCallback(async () => {
    setStartingNewQuote(true);

    const id = await startNewQuoteRaw();

    if (id) {
      showNotification('New quote created successfully');
      onRemount(id, false);
    }

    setStartingNewQuote(false);

    return id;
  }, [startNewQuoteRaw, showNotification, onRemount]);

  const selectSavedProposal = useCallback(
    (proposalId: string) => {
      onRemount(proposalId);
    },
    [onRemount]
  );

  const isInitialLoading = dealBuilderLoading || startingNewQuote;

  const handleNavigateToAudit = () => {
    setRender(prevState => (prevState === 'builder' ? 'log' : 'builder'));
  };

  if (!activeQuoteId || !activeEntity?.proposal?.proposalId) return null;

  const renderByType: Record<'builder' | 'log', ReactNode> = {
    builder: (
      <>
        <DealBuilderSteps
          navigationNode={action => (
            <StepFooterNav action={action} quoteId={activeQuoteId} proposalId={activeEntity.proposal.proposalId} />
          )}
        />
        <QuoteHistory historyEntities={historyEntities} selectSavedProposal={selectSavedProposal} />
      </>
    ),
    log: activeEntity?.quote?.quoteId && <QuoteChangelogListing quoteId={activeEntity?.quote?.quoteId} />,
  };

  return (
    <DealBuilderContextProvider
      value={{
        activeEntity,
        startNewQuote,
        setActiveEntity,
        contacts: { contactsData },
        pricing: { priceCalc, isQuoteCalcLoading },
        sourceData: {
          vo,
          creditApplication,
          equipments: equipmentData,
          voId: vo?.vendorOpportunityId ?? '',
          dynamicsVOId: vo?.dynamicsVendorOpportunityId ?? '',
        },
        equipment: {
          isEquipmentSaving,
          setEquipmentSaving,
          selectedEquipmentItem,
          setSelectedEquipmentItem: handleSetSelectedEquipmentItem,
        },
      }}
    >
      {isInitialLoading ? (
        <DealBuilderSkeleton />
      ) : (
        <Stack gap={2}>
          <BuildAQuoteCard
            auditLogNode={
              <Button
                size="small"
                startIcon={<Restore />}
                onClick={handleNavigateToAudit}
                disabled={!activeEntity?.quote?.quoteId}
              >
                {render === 'builder' ? 'Audit Log' : 'Back to Builder'}
              </Button>
            }
          />

          {renderByType[render]}
        </Stack>
      )}
    </DealBuilderContextProvider>
  );
};
